/**
 * Theme: the wishlist page.
 *
 * READ, RESOLVE, RENDER. The saved list is browser state, so the server can
 * only ship the shell (sections/core-wishlist.liquid). This script reads the
 * keys, turns each one into live Shopify data, and injects the SHARED product
 * card for it.
 *
 * THE RESOLVE IS TWO HOPS, AND BOTH WERE CHOSEN AGAINST MEASUREMENTS.
 *
 *   1. HEAD /variants/<id>, following the redirect, and read the final URL for
 *      the product handle. Measured on this store: 200 in about 190ms with no
 *      body, against 536KB if the same request is made as a GET. It doubles as
 *      the existence check, because a deleted or unpublished variant 404s.
 *   2. GET /products/<handle>?section_id=core-wishlist-card, which renders
 *      snippets/core-product-card.liquid for that product.
 *
 * Two things were ruled out by testing rather than by opinion. /variants/<id>
 * with a section_id returns 404, so the hops cannot be collapsed. And an
 * arbitrary query parameter is invisible to Liquid, verified by fetching a
 * section with and without one and getting byte identical output, so a section
 * cannot be handed the whole list of ids in a single request. One request per
 * card is the floor, not a shortcut.
 *
 * Handles are cached in sessionStorage, so hop 1 happens once per variant per
 * session. Handles change rarely and a stale one simply 404s on hop 2, which is
 * handled.
 *
 * NO CARD MARKUP IS BUILT HERE, deliberately and permanently. The card is
 * Liquid, once, in snippets/core-product-card.liquid. Everything below either
 * asks the server for a card or annotates one it was given.
 */
(function () {
  'use strict';

  /** The section FILE NAME, which is the Section Rendering contract. */
  var CORE_WISHLIST_CARD_SECTION = 'core-wishlist-card';
  // Renamed from 'ats_wl_handles' in de-brand step 4d with NO migration shim, on
  // purpose: this is a per-tab session cache that rebuilds itself on first use.
  var HANDLE_CACHE_KEY = 'core_wl_handles';

  var root = document.querySelector('[data-wishlist-root]');
  if (!root) return;

  var grid = root.querySelector('[data-wishlist-grid]');
  // data-wishlist-TOTAL, not -count: the header badge owns -count and
  // assets/core-header.js writes the bare number into every element with it.
  var countEl = root.querySelector('[data-wishlist-total]');
  var statusEl = root.querySelector('[data-wishlist-status]');

  // True while the first resolve is in flight. Pruning a dead key calls
  // WISH.remove, which announces wishlist:change, which would otherwise
  // re-enter the reconcile below while the grid is still being built.
  var resolving = false;

  // ---- storage helpers ----------------------------------------------------

  function readHandleCache() {
    try {
      var raw = window.sessionStorage.getItem(HANDLE_CACHE_KEY);
      var parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  function writeHandleCache(map) {
    try {
      window.sessionStorage.setItem(HANDLE_CACHE_KEY, JSON.stringify(map));
    } catch (error) {
      /* Private mode or a full quota. The page still works, just uncached. */
    }
  }

  /** `variantId|colour|size`, with blanks preserved. */
  function partsOf(key) {
    var bits = String(key).split('|');
    return { key: key, id: bits[0] || '', colour: bits[1] || '', size: bits[2] || '' };
  }

  // ---- resolve ------------------------------------------------------------

  /**
   * Variant id to product handle. Resolves to null when the variant is gone,
   * which is the caller's signal to prune.
   */
  function handleFor(id, cache) {
    if (cache[id]) return Promise.resolve(cache[id]);

    return fetch(window.Shopify.routes.root + 'variants/' + encodeURIComponent(id), {
      method: 'HEAD',
      redirect: 'follow',
    })
      .then(function (response) {
        if (!response.ok) return null;
        var match = /\/products\/([^/?#]+)/.exec(response.url || '');
        if (!match) return null;
        cache[id] = match[1];
        return match[1];
      })
      .catch(function () {
        return null;
      });
  }

  /** One saved key to card markup, or null if it cannot be resolved. */
  function cardFor(parts, cache) {
    return handleFor(parts.id, cache).then(function (handle) {
      if (!handle) return null;

      // &variant= IS WHAT MAKES THE TILE SHOW THE SAVED COLOUR.
      //
      // Without it the section renders product.selected_or_first_available_
      // variant, so a product saved in three colours showed the default colour
      // on all three cards. With it, product.selected_variant is set inside the
      // section and snippets/core-product-card.liquid renders that variant's own
      // image. Verified: the same section rendered with three colour variant
      // ids came back with data-variant-id matching each one.
      //
      // The id is always present, because every saved key carries one: an
      // item-only card save stores the default variant's id, so those cards
      // keep showing the default colour, which is correct.
      var url =
        window.Shopify.routes.root +
        'products/' +
        handle +
        '?section_id=' +
        CORE_WISHLIST_CARD_SECTION +
        '&variant=' +
        encodeURIComponent(parts.id);

      return fetch(url)
        .then(function (response) {
          if (!response.ok) return null;
          return response.text();
        })
        .then(function (html) {
          if (!html || !html.trim()) return null;
          return { parts: parts, html: html, handle: handle };
        })
        .catch(function () {
          return null;
        });
    });
  }

  // ---- the add control ----------------------------------------------------

  /**
   * DECIDE WHAT THE BUTTON IS ALLOWED TO DO, AND SAY SO ON ITS FACE.
   *
   * It may add only when the add is unambiguous: the product has nothing to
   * choose, or the saved key names a variant that is genuinely in stock. In
   * every other case it goes to the product page, where the size guide, the
   * size finder and the select-a-size nudge already are.
   *
   * The label always matches the action. A button reading "Add to bag" that
   * navigates instead is a small lie, and one that quietly picks a size nobody
   * chose is how a wishlist generates returns.
   */
  function decideAdd(button, parts) {
    // True when the product has exactly one variant, so there is nothing to
    // choose whatever its options are called. Not has_only_default_variant:
    // see the note in snippets/core-product-card.liquid.
    var single = button.getAttribute('data-wish-single') === 'true';
    var names = (button.getAttribute('data-wish-options') || '').split('|').filter(Boolean);

    var variants = [];
    try {
      variants = JSON.parse(button.getAttribute('data-wish-variants') || '[]');
    } catch (error) {
      variants = [];
    }

    var inStock = false;
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(parts.id)) {
        inStock = Boolean(variants[i].a);
        break;
      }
    }

    var soldOut = { mode: 'link', label: 'Sold out, see options', variant: true };

    if (single) {
      return inStock ? { mode: 'add', label: 'Add to bag' } : soldOut;
    }

    var needColour = false;
    var needSize = false;
    var unrepresentable = false;

    names.forEach(function (name) {
      if (name === 'colour' || name === 'color') needColour = true;
      else if (name === 'size') needSize = true;
      // A third option, or one this two slot key cannot carry. Never guess it.
      else unrepresentable = true;
    });

    if (unrepresentable) return { mode: 'link', label: 'Choose options' };

    var missing = [];
    if (needColour && !parts.colour) missing.push('colour');
    if (needSize && !parts.size) missing.push('size');

    if (missing.length > 1) return { mode: 'link', label: 'Choose options' };
    if (missing.length === 1) {
      return { mode: 'link', label: missing[0] === 'size' ? 'Choose size' : 'Choose colour' };
    }

    return inStock ? { mode: 'add', label: 'Add to bag' } : soldOut;
  }

  /**
   * ADD THROUGH THE STORE'S ONE ADD PATH, window.CORECart.add.
   *
   * This used to post /cart/add.js here and dispatch a bare CustomEvent. The
   * cart updated and the header badge moved, so it looked right, but THE
   * DRAWER NEVER OPENED. Horizon's cart-drawer.js gates its auto-open on
   *
   *     this.hasAttribute('auto-open') && event.action === 'add' && !isOpen
   *
   * and `action` is a property on the event, not something in `detail`. A hand
   * built event has none, so the condition was false every time. The attribute
   * was never the problem: snippets/core-cart-drawer.liquid has carried
   * `auto-open` all along.
   *
   * Routing through assets/core-cart.js fixes the ordering as well as the open.
   * mutate() requests the drawer section in the SAME POST and swaps the
   * returned HTML in before it announces, so the drawer already holds the new
   * line when cart-drawer.js opens it. Sequencing by construction, not by a
   * timer, and there is now one add implementation in the theme instead of two.
   */
  function addToBag(button, parts) {
    // The drawer, and with it assets/core-cart.js, renders on every template
    // except /cart, so this is present on the wishlist. If it somehow is not,
    // fall back to the product page rather than to a silent add with no drawer,
    // which is the failure this whole function exists to remove.
    if (!window.CORECart || typeof window.CORECart.add !== 'function') {
      var fallback = button.getAttribute('data-wish-url');
      if (fallback) window.location.href = fallback;
      return;
    }

    button.disabled = true;

    window.CORECart.add(parts.id, 1)
      .then(function () {
        button.textContent = 'Added';
        say('Added to your bag.');
        window.setTimeout(function () {
          button.textContent = 'Add to bag';
          button.disabled = false;
        }, 1600);
      })
      .catch(function () {
        // Honest failure: send them where they can complete it by hand rather
        // than leaving a button that appears to have done nothing.
        button.textContent = 'View product';
        button.disabled = false;
      });
  }

  // ---- painting one injected card ----------------------------------------

  /**
   * Annotate a freshly injected card with the things only the browser knows:
   * which key it stands for, the saved colour and size, and what its add
   * control is allowed to do.
   */
  function dressCard(cell, parts) {
    cell.setAttribute('data-wish-key', parts.key);

    var save = cell.querySelector('[data-core-card-save]');
    if (save) {
      // THE BOOKMARK MUST CARRY ALL THREE PARTS OF THE STORED KEY, INCLUDING
      // THE VARIANT ID, AND THE ID IS THE ONE THAT IS EASY TO MISS.
      //
      // The shared card renders its own id from
      // `product.selected_or_first_available_variant`, which is right for every
      // other surface: a rail card saves the product, so the default variant is
      // the sensible handle for it. Here it is wrong, because the card was
      // fetched by product handle with no `?variant=`, so the server has no way
      // to know WHICH saved variant this tile stands for and always renders the
      // first available one.
      //
      // Setting only colour and size left the button keyed on
      // `<defaultVariantId>|<savedColour>|<savedSize>`. For a saved NON-default
      // variant that key is not in the store, so clicking the bookmark to
      // remove the item ADDED a phantom entry and left the real one in place.
      // Traced live: a stored `58241584922956|Navy|XXL` produced
      // `toggle("58241584005452","Navy","XXL") -> true`, taking the list from
      // one entry to two. Removing everything was therefore impossible.
      //
      // Overriding the id here rather than in the card keeps the card correct
      // for every other surface: the wishlist is the only place that knows
      // which variant a tile represents.
      save.setAttribute('data-variant-id', parts.id);
      save.setAttribute('data-wish-colour', parts.colour);
      save.setAttribute('data-wish-size', parts.size);
      if (window.CORECardSave) window.CORECardSave.paint(save, true);
    }

    var slot = cell.querySelector('[data-wish-variant]');
    if (slot) {
      var bits = [];
      if (parts.colour) bits.push(parts.colour);
      if (parts.size) bits.push(parts.size);
      // Blank for an item-only save. The slot keeps its reserved height either
      // way, so cards stay the same height whatever each entry captured.
      slot.textContent = bits.join(' · ');
    }

    var title = cell.querySelector('.pc-title');
    var add = cell.querySelector('[data-wish-add]');
    if (!add) return;

    var decision = decideAdd(add, parts);
    var url = add.getAttribute('data-wish-url') || '';

    // Land on the saved variant where the key names one, so the shopper
    // arrives with their colour and size already selected.
    var deepLink = decision.variant && parts.id ? url + '?variant=' + encodeURIComponent(parts.id) : url;
    if (title && parts.colour && parts.id) {
      title.setAttribute('href', url + '?variant=' + encodeURIComponent(parts.id));
    }

    add.textContent = decision.label;
    add.hidden = false;

    if (decision.mode === 'add') {
      add.setAttribute('data-wish-mode', 'add');
    } else {
      add.setAttribute('data-wish-mode', 'link');
      add.setAttribute('data-wish-href', deepLink);
    }
  }

  // ---- render -------------------------------------------------------------

  function say(message) {
    if (statusEl) statusEl.textContent = message;
  }

  function setCount(n) {
    if (countEl) countEl.textContent = n > 0 ? '(' + n + ')' : '';
  }

  function showEmpty() {
    root.classList.remove('is-loading');
    root.classList.add('core-wishlist--empty');
    setCount(0);
  }

  function showGrid() {
    root.classList.remove('is-loading');
    root.classList.remove('core-wishlist--empty');
  }

  function render() {
    if (!window.WISH) return;

    var keys = window.WISH.all();
    if (!keys.length) {
      grid.innerHTML = '';
      showEmpty();
      return;
    }

    root.classList.add('is-loading');
    root.classList.remove('core-wishlist--empty');
    resolving = true;

    var cache = readHandleCache();

    // Resolved concurrently, then written in STORED ORDER, so the grid does not
    // shuffle itself according to which request happened to finish first.
    Promise.all(
      keys.map(function (key) {
        return cardFor(partsOf(key), cache);
      })
    ).then(function (results) {
      writeHandleCache(cache);

      var dead = [];
      var html = '';
      var live = [];

      results.forEach(function (result, index) {
        if (!result) {
          dead.push(partsOf(keys[index]));
          return;
        }
        html += '<div class="wl-cell">' + result.html + '</div>';
        live.push(result.parts);
      });

      grid.innerHTML = html;

      var cells = grid.querySelectorAll('.wl-cell');
      for (var i = 0; i < cells.length; i++) {
        dressCard(cells[i], live[i]);
      }

      // PRUNE. A key that cannot resolve is a product that no longer exists, so
      // it is dropped from storage rather than retried on every visit. Done
      // after the render so the page never waits on it.
      resolving = false;
      dead.forEach(function (parts) {
        window.WISH.remove(parts.id, parts.colour, parts.size);
      });

      if (!live.length) {
        showEmpty();
        return;
      }

      showGrid();
      setCount(live.length);
    });
  }

  // ---- events -------------------------------------------------------------

  grid.addEventListener('click', function (event) {
    var add = event.target.closest('[data-wish-add]');
    if (!add) return;

    event.preventDefault();

    var cell = add.closest('.wl-cell');
    if (!cell) return;
    var parts = partsOf(cell.getAttribute('data-wish-key') || '');

    if (add.getAttribute('data-wish-mode') === 'add') {
      addToBag(add, parts);
      return;
    }

    var href = add.getAttribute('data-wish-href');
    if (href) window.location.href = href;
  });

  /**
   * RECONCILE, DO NOT REFETCH.
   *
   * The bookmark on a card is handled by assets/core-card-save.js, which knows
   * nothing about this page: it toggles the store and repaints the button. All
   * that is left here is to take the tile away and update the count, which is a
   * DOM operation, not a network one. Re-resolving every card to remove one
   * would be a round trip per remaining item for no new information.
   *
   * Keys that appear rather than disappear (a save made in another tab) do need
   * resolving, so those fall through to a full render.
   */
  document.addEventListener('wishlist:change', function () {
    if (resolving || !window.WISH) return;

    var keys = window.WISH.all();
    var cells = grid.querySelectorAll('.wl-cell');
    var rendered = [];
    var removedAny = false;

    for (var i = 0; i < cells.length; i++) {
      var key = cells[i].getAttribute('data-wish-key');
      rendered.push(key);
      if (keys.indexOf(key) === -1) {
        cells[i].remove();
        removedAny = true;
      }
    }

    var added = keys.filter(function (key) {
      return rendered.indexOf(key) === -1;
    });

    if (added.length) {
      render();
      return;
    }

    if (!removedAny) return;

    var left = grid.querySelectorAll('.wl-cell').length;
    setCount(left);
    say(left === 1 ? '1 item saved.' : left + ' items saved.');
    if (!left) showEmpty();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', render);
  } else {
    render();
  }
})();
